/**
 * VINYL CALCULATOR — Jun 2026 (V1, aislado).
 * Calculadora de Printed Vinyl: medidas con unidades independientes (in/ft/cm),
 * múltiples tamaños, rotación y panelización automáticas (rollo útil 53"/51",
 * overlap 1"), tiers por ft² terminados combinados, surcharge por material,
 * acabados con mínimos por trabajo y mínimo de vinyl $30/job.
 * Emite UN EstimateItem (quantity=1, unit_price=total) con metadata `vinyl_calc`.
 * Autocontenido: NO toca commercialEngine ni backend.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const OVERLAP_IN = 1;
const MIN_VINYL_CHARGE = 30;
// Bloque 1 Partner Points — los costos internos (material, waste, finishing)
// ya NO viven en el bundle: se cargan en runtime desde GET /api/internal-costs
// (los partners no los reciben; su costo se resuelve server-side).
const ZERO_INTERNAL = {
  material_cost: { regular: 0, laminated: 0 },
  waste_factor: 1,
  finishing_cost: { contour: 0, weeding: 0 },
};
const REGULAR_TIERS = [
  { max: 10, rate: 12.0, label: "1–10 ft²" },
  { max: 25, rate: 11.0, label: "11–25 ft²" },
  { max: 50, rate: 10.0, label: "26–50 ft²" },
  { max: Infinity, rate: 9.0, label: "51+ ft²" },
];
const LAMINATED_TIERS = [
  { max: 9, rate: 13.5, label: "1–9 ft²" },
  { max: 24, rate: 13.0, label: "10–24 ft²" },
  { max: 49, rate: 12.5, label: "25–49 ft²" },
  { max: 99, rate: 11.5, label: "50–99 ft²" },
  { max: Infinity, rate: 10.5, label: "100+ ft²" },
];
const MATERIALS = {
  regular: { label: "Regular Printable Vinyl", surcharge: 0 },
  window_perf: { label: "Microperforated / Window Perf", surcharge: 1.0 },
  wallpaper: { label: "Wallpaper", surcharge: 2.0 },
};
const FINISHING = {
  contour: { rate: 1.5, min: 10 },
  weeding: { rate: 1.5, min: 10 },
};
const UNITS = ["in", "ft", "cm"];

export function toFeet(value, unit) {
  const v = Number(value);
  if (unit === "in") return v / 12;
  if (unit === "cm") return v / 30.48;
  return v;
}

export function findVinylTier(totalSqft, tiers) {
  return tiers.find((t) => totalSqft <= t.max) || tiers[tiers.length - 1];
}

// Rotación + panelización automáticas: evalúa ambas orientaciones; sin
// paneles si alguna cabe (empate → menor longitud de rollo); si no cabe,
// menos paneles y en empate menor área de producción.
export function planPanels(wIn, hIn, usableIn) {
  const opts = [
    { cross: wIn, along: hIn, orientation: "normal" },
    { cross: hIn, along: wIn, orientation: "rotated" },
  ].map((o) => {
    if (o.cross <= usableIn) {
      return { ...o, panels: 1, prodIn2: wIn * hIn, lengthIn: o.along };
    }
    const panels = Math.ceil((o.cross - usableIn) / (usableIn - OVERLAP_IN)) + 1;
    return {
      ...o,
      panels,
      prodIn2: o.along * (o.cross + (panels - 1) * OVERLAP_IN),
      lengthIn: o.along * panels,
    };
  });
  opts.sort(
    (a, b) => a.panels - b.panels || a.prodIn2 - b.prodIn2 || a.lengthIn - b.lengthIn
  );
  return opts[0];
}

export function computeVinylJob({ sizes, material, lamination, contour, weeding }, ic = ZERO_INTERNAL) {
  const usable = contour ? 51 : 53;
  let finished = 0;
  let production = 0;
  const lines = sizes.map((s) => {
    const qty = Number(s.qty);
    const wFt = toFeet(s.w, s.wu);
    const hFt = toFeet(s.h, s.hu);
    const plan = planPanels(wFt * 12, hFt * 12, usable);
    const areaPiece = wFt * hFt;
    const fin = areaPiece * qty;
    const prod = (plan.prodIn2 / 144) * qty;
    finished += fin;
    production += prod;
    return {
      quantity: qty,
      original_width: Number(s.w),
      original_width_unit: s.wu,
      original_height: Number(s.h),
      original_height_unit: s.hu,
      converted_width_ft: wFt,
      converted_height_ft: hFt,
      area_per_piece_sqft: areaPiece,
      finished_area_sqft: fin,
      production_area_sqft: prod,
      orientation: plan.orientation,
      paneling_required: plan.panels > 1,
      panel_count: plan.panels,
    };
  });
  const tier = findVinylTier(finished, lamination ? LAMINATED_TIERS : REGULAR_TIERS);
  const surcharge = MATERIALS[material].surcharge;
  const effRate = tier.rate + surcharge;
  const baseRaw = finished * effRate;
  const vinylBase = Math.max(baseRaw, MIN_VINYL_CHARGE);
  const contourCharge = contour
    ? Math.max(finished * FINISHING.contour.rate, FINISHING.contour.min)
    : 0;
  const weedingCharge = weeding
    ? Math.max(finished * FINISHING.weeding.rate, FINISHING.weeding.min)
    : 0;
  const total = Math.round((vinylBase + contourCharge + weedingCharge) * 100) / 100;
  const matCost =
    production *
    (lamination ? ic.material_cost.laminated : ic.material_cost.regular) *
    ic.waste_factor;
  const internalCost =
    Math.round(
      (matCost +
        (contour ? finished * ic.finishing_cost.contour : 0) +
        (weeding ? finished * ic.finishing_cost.weeding : 0)) *
        100
    ) / 100;
  return {
    lines,
    total_finished_area: finished,
    total_production_area: production,
    usable_roll_width: usable,
    pricing_tier: tier.label,
    base_rate_per_sqft: tier.rate,
    material_surcharge_per_sqft: surcharge,
    effective_rate_per_sqft: effRate,
    vinyl_base_charge: Math.round(vinylBase * 100) / 100,
    minimum_applied: baseRaw < MIN_VINYL_CHARGE,
    contour_charge: Math.round(contourCharge * 100) / 100,
    weeding_charge: Math.round(weedingCharge * 100) / 100,
    internal_cost: internalCost,
    total_price: total,
  };
}

const fmt = (v, d = 2) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
const money = (v) => `$${fmt(v)}`;

const emptySize = () => ({ qty: "1", w: "", wu: "in", h: "", hu: "in" });

const sizeValid = (s) => {
  const q = Number(s.qty);
  const w = Number(s.w);
  const h = Number(s.h);
  return (
    Number.isFinite(q) && q > 0 &&
    Number.isFinite(w) && w > 0 &&
    Number.isFinite(h) && h > 0 &&
    UNITS.includes(s.wu) && UNITS.includes(s.hu)
  );
};

export default function VinylCalculatorModal({ onClose, onAdd }) {
  const { t } = useI18n();
  const [material, setMaterial] = useState("regular");
  const [sizes, setSizes] = useState([emptySize()]);
  const [lamination, setLamination] = useState(false);
  const [contour, setContour] = useState(false);
  const [weeding, setWeeding] = useState(false);
  const [internalCosts, setInternalCosts] = useState(ZERO_INTERNAL);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/internal-costs`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled && data?.vinyl) setInternalCosts(data.vinyl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const allValid = sizes.length > 0 && sizes.every(sizeValid);
  const calc = useMemo(
    () =>
      allValid
        ? computeVinylJob({ sizes, material, lamination, contour, weeding }, internalCosts)
        : null,
    [allValid, sizes, material, lamination, contour, weeding, internalCosts]
  );

  const setSize = (i, patch) =>
    setSizes((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const sizesText = (sz) =>
    sz
      .map(
        (s) =>
          `${Number(s.qty)} ${Number(s.qty) === 1 ? "pc" : "pcs"} — ${s.w} ${s.wu} × ${s.h} ${s.hu}`
      )
      .join(" · ");

  const handleAdd = () => {
    if (!calc) {
      toast.error(t("vinyl_invalid"));
      return;
    }
    const matLabel = MATERIALS[material].label;
    const finishing = [
      lamination && "Lamination",
      contour && "Contour Cut",
      weeding && "Weeding + Transfer Tape",
    ].filter(Boolean);
    const customerNotes = [sizesText(sizes), ...finishing].join(" · ");
    const prodLines = calc.lines
      .map(
        (l, i) =>
          `#${i + 1}: ${l.quantity} ${l.quantity === 1 ? "pc" : "pcs"} — ` +
          `${l.original_width} ${l.original_width_unit} × ${l.original_height} ${l.original_height_unit} ` +
          `→ ${fmt(l.converted_width_ft)} ft × ${fmt(l.converted_height_ft)} ft | ` +
          (l.paneling_required
            ? `${l.panel_count} ${t("vinyl_panels_label")} (${l.orientation})`
            : `${t("vinyl_no_panels")} (${l.orientation})`)
      )
      .join("\n");
    const internalNotes =
      `[VINYL] Material: ${matLabel} | Lamination: ${lamination ? "Sí" : "No"} | ` +
      `Contour Cut: ${contour ? "Sí" : "No"} | Weeding+Tape: ${weeding ? "Sí" : "No"}\n` +
      `Rollo útil: ${calc.usable_roll_width} in | Overlap paneles: ${OVERLAP_IN} in\n` +
      `${prodLines}\n` +
      `Finished: ${fmt(calc.total_finished_area)} ft² | Production: ${fmt(calc.total_production_area)} ft² | ` +
      `Tier: ${calc.pricing_tier} @ $${fmt(calc.effective_rate_per_sqft)}/ft²`;
    onAdd({
      description: `Printed Vinyl — ${matLabel}`,
      quantity: 1,
      unit_price: calc.total_price,
      unit_cost: calc.internal_cost,
      taxable: true,
      source_type: "vinyl_calculator",
      manual_price_override: true,
      price_manual: true,
      customer_notes: customerNotes,
      internal_notes: internalNotes,
      notes: internalNotes,
      vinyl_calc: {
        material,
        material_label: matLabel,
        lamination,
        contour_cut: contour,
        weeding_transfer_tape: weeding,
        usable_roll_width: calc.usable_roll_width,
        overlap_inches: OVERLAP_IN,
        pricing_tier: calc.pricing_tier,
        base_rate_per_sqft: calc.base_rate_per_sqft,
        material_surcharge_per_sqft: calc.material_surcharge_per_sqft,
        effective_rate_per_sqft: calc.effective_rate_per_sqft,
        total_finished_area: calc.total_finished_area,
        total_production_area: calc.total_production_area,
        vinyl_base_charge: calc.vinyl_base_charge,
        minimum_applied: calc.minimum_applied,
        contour_charge: calc.contour_charge,
        weeding_charge: calc.weeding_charge,
        internal_cost: calc.internal_cost,
        total_price: calc.total_price,
        sizes: calc.lines,
      },
    });
    onClose();
  };

  return (
    <div
      data-testid="vinyl-calc-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="vinyl-calc-modal"
        className="bg-white w-full max-w-2xl max-h-[92vh] overflow-auto border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-black flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">GP / PRINTED VINYL</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">
              {t("vinyl_calc_title")}
            </h2>
          </div>
          <button
            type="button"
            data-testid="vinyl-calc-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <label className="block">
            <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-1">
              {t("vinyl_material").toUpperCase()}
            </div>
            <select
              data-testid="vinyl-material-select"
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              className="w-full border border-[var(--border)] px-2 py-2 text-sm"
            >
              <option value="regular">{MATERIALS.regular.label}</option>
              <option value="window_perf">
                {MATERIALS.window_perf.label} (+$1.00/ft²)
              </option>
              <option value="wallpaper">{MATERIALS.wallpaper.label} (+$2.00/ft²)</option>
            </select>
          </label>

          <div className="space-y-3">
            {sizes.map((s, i) => (
              <div
                key={i}
                data-testid={`vinyl-size-row-${i}`}
                className="border border-[var(--border)] p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)]">
                    {t("vinyl_size").toUpperCase()} #{i + 1}
                  </div>
                  {sizes.length > 1 && (
                    <button
                      type="button"
                      data-testid={`vinyl-size-remove-${i}`}
                      onClick={() => setSizes((arr) => arr.filter((_, x) => x !== i))}
                      className="text-xs text-[var(--alert-overdue,#c00)] hover:underline"
                    >
                      {t("vinyl_remove_size")}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                  <label className="block">
                    <div className="text-[0.6rem] font-mono-tech font-bold text-[var(--text-secondary)] mb-1">
                      {t("vinyl_qty").toUpperCase()}
                    </div>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={s.qty}
                      onChange={(e) => setSize(i, { qty: e.target.value })}
                      data-testid={`vinyl-size-qty-${i}`}
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm text-right"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[0.6rem] font-mono-tech font-bold text-[var(--text-secondary)] mb-1">
                      {t("vinyl_width").toUpperCase()}
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={s.w}
                      onChange={(e) => setSize(i, { w: e.target.value })}
                      data-testid={`vinyl-size-width-${i}`}
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm text-right"
                    />
                  </label>
                  <select
                    value={s.wu}
                    onChange={(e) => setSize(i, { wu: e.target.value })}
                    data-testid={`vinyl-size-width-unit-${i}`}
                    className="border border-[var(--border)] px-2 py-1.5 text-sm"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  <label className="block">
                    <div className="text-[0.6rem] font-mono-tech font-bold text-[var(--text-secondary)] mb-1">
                      {t("vinyl_height").toUpperCase()}
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={s.h}
                      onChange={(e) => setSize(i, { h: e.target.value })}
                      data-testid={`vinyl-size-height-${i}`}
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm text-right"
                    />
                  </label>
                  <select
                    value={s.hu}
                    onChange={(e) => setSize(i, { hu: e.target.value })}
                    data-testid={`vinyl-size-height-unit-${i}`}
                    className="border border-[var(--border)] px-2 py-1.5 text-sm"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
            <button
              type="button"
              data-testid="vinyl-add-size"
              onClick={() => setSizes((arr) => [...arr, emptySize()])}
              className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
            >
              {t("vinyl_add_size")}
            </button>
          </div>

          <div>
            <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-2">
              {t("vinyl_finishing").toUpperCase()}
            </div>
            <div className="space-y-1.5 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={lamination}
                  onChange={(e) => setLamination(e.target.checked)}
                  data-testid="vinyl-lamination"
                />
                {t("vinyl_lamination")}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={contour}
                  onChange={(e) => setContour(e.target.checked)}
                  data-testid="vinyl-contour"
                />
                Contour Cut (+$1.50/ft², min $10)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={weeding}
                  onChange={(e) => setWeeding(e.target.checked)}
                  data-testid="vinyl-weeding"
                />
                Weeding + Transfer Tape (+$1.50/ft², min $10)
              </label>
            </div>
          </div>

          {calc && (
            <div
              data-testid="vinyl-summary"
              className="border-l-4 border-black bg-[var(--bg,#fafafa)] px-4 py-3 space-y-1 text-sm"
            >
              <div className="flex justify-between">
                <span>{t("vinyl_finished_area")}</span>
                <span className="font-mono-tech tabular-nums font-bold" data-testid="vinyl-finished-area">
                  {fmt(calc.total_finished_area)} ft²
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t("vinyl_production_area")}</span>
                <span className="font-mono-tech tabular-nums" data-testid="vinyl-production-area">
                  {fmt(calc.total_production_area)} ft²
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t("vinyl_tier")}</span>
                <span className="font-mono-tech" data-testid="vinyl-tier">
                  {calc.pricing_tier} · ${fmt(calc.base_rate_per_sqft)}/ft²
                  {calc.material_surcharge_per_sqft > 0 &&
                    ` + $${fmt(calc.material_surcharge_per_sqft)} = $${fmt(calc.effective_rate_per_sqft)}/ft²`}
                </span>
              </div>
              {calc.lines.some((l) => l.paneling_required) && (
                <div className="flex justify-between">
                  <span>{t("vinyl_paneling")}</span>
                  <span className="font-mono-tech" data-testid="vinyl-paneling-info">
                    {calc.lines
                      .map((l, i) =>
                        l.paneling_required
                          ? `#${i + 1}: ${l.panel_count} ${t("vinyl_panels_label")}`
                          : null
                      )
                      .filter(Boolean)
                      .join(" · ")}{" "}
                    ({calc.usable_roll_width}″ · overlap 1″)
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>
                  {t("vinyl_base_charge")}
                  {calc.minimum_applied && (
                    <span className="ml-1 text-xs text-[var(--text-secondary)]" data-testid="vinyl-min-applied">
                      ({t("vinyl_min_applied")})
                    </span>
                  )}
                </span>
                <span className="font-mono-tech tabular-nums" data-testid="vinyl-base-charge">
                  {money(calc.vinyl_base_charge)}
                </span>
              </div>
              {calc.contour_charge > 0 && (
                <div className="flex justify-between">
                  <span>Contour Cut</span>
                  <span className="font-mono-tech tabular-nums" data-testid="vinyl-contour-charge">
                    {money(calc.contour_charge)}
                  </span>
                </div>
              )}
              {calc.weeding_charge > 0 && (
                <div className="flex justify-between">
                  <span>Weeding + Transfer Tape</span>
                  <span className="font-mono-tech tabular-nums" data-testid="vinyl-weeding-charge">
                    {money(calc.weeding_charge)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-[var(--border)] pt-1 font-bold">
                <span>{t("vinyl_total")}</span>
                <span className="font-mono-tech tabular-nums" data-testid="vinyl-total">
                  {money(calc.total_price)}
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="vinyl-cancel"
              onClick={onClose}
              className="font-mono-tech text-xs font-bold px-4 py-2 border border-black hover:bg-black hover:text-white"
            >
              {t("vinyl_cancel")}
            </button>
            <button
              type="button"
              data-testid="vinyl-add-to-estimate"
              onClick={handleAdd}
              disabled={!calc}
              className="font-mono-tech text-xs font-bold px-4 py-2 bg-black text-white border border-black hover:bg-[var(--primary,#e5007d)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("vinyl_add_to_estimate")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
